import { Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BackToTopButton } from "@/components/BackToTopButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Terms of Service | Grounded"
        description="The terms that govern your use of Grounded — the community-powered guide to work-friendly cafes."
        path="/terms"
      />
      <Header />
      <main className="container max-w-3xl mx-auto px-6 py-10 md:py-16">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
          Terms of Service
        </h1>
        <p className="text-muted-foreground mb-8">
          Effective date: February 23, 2026 ·{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </p>

        <section className="prose-section space-y-6 text-foreground/90 text-[15px] leading-relaxed">
          <p>
            Welcome to Grounded. By using our application you agree to these Terms of Service. If you do not agree, please do not use the app.
          </p>

          <Accordion type="multiple" className="w-full">
            {/* 1 */}
            <AccordionItem value="accounts">
              <AccordionTrigger className="text-base font-semibold">
                1. Accounts &amp; Responsibilities
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <p>You must provide accurate information when creating an account. You are responsible for maintaining the security of your credentials and for all activity under your account.</p>
              </AccordionContent>
            </AccordionItem>

            {/* 2 */}
            <AccordionItem value="ugc">
              <AccordionTrigger className="text-base font-semibold">
                2. User-Generated Content
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <p>
                  You retain ownership of content you submit (photos, reviews, check-in notes). By posting content, you grant Grounded a non-exclusive, worldwide, royalty-free license to use, display, and distribute that content within the app and for promotional purposes.
                </p>
                <p>
                  You represent that you have the right to share any content you upload and that it does not violate any third party's rights.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* 3 */}
            <AccordionItem value="conduct">
              <AccordionTrigger className="text-base font-semibold">
                3. Prohibited Conduct
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <p>You agree not to:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Post false, misleading, or spam reviews</li>
                  <li>Upload inappropriate, offensive, or illegal content</li>
                  <li>Harass or impersonate other users</li>
                  <li>Attempt to access another user's account</li>
                  <li>Interfere with the operation of the app or its infrastructure</li>
                  <li>Scrape or bulk-download data from the app</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 4 */}
            <AccordionItem value="disclaimers">
              <AccordionTrigger className="text-base font-semibold">
                4. Disclaimers
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <p>
                  Grounded is provided "as is" without warranties of any kind. Cafe information, WiFi speeds, noise levels, and work-readiness scores are community-contributed and may not be accurate or up to date. We are not responsible for your experience at any cafe listed on the platform.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* 5 */}
            <AccordionItem value="liability">
              <AccordionTrigger className="text-base font-semibold">
                5. Limitation of Liability
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <p>
                  To the maximum extent permitted by law, Grounded and its operators shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the app.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* 6 */}
            <AccordionItem value="termination">
              <AccordionTrigger className="text-base font-semibold">
                6. Termination
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <p>
                  We may suspend or terminate your account at any time if you violate these terms or engage in conduct that we determine is harmful to other users or the platform. You may delete your account at any time from the Profile page.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* 7 */}
            <AccordionItem value="changes">
              <AccordionTrigger className="text-base font-semibold">
                7. Changes to These Terms
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-muted-foreground">
                <p>
                  We may update these Terms from time to time. Continued use of the app after changes constitutes acceptance of the revised terms.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* 8 */}
            <AccordionItem value="contact">
              <AccordionTrigger className="text-base font-semibold">
                8. Contact
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-muted-foreground">
                <p>
                  Questions about these terms? Use the in-app feedback form or email{" "}
                  <a href="mailto:hello@getgrounded.app" className="text-primary hover:underline">
                    hello@getgrounded.app
                  </a>.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
      </main>
      <Footer />
      <BackToTopButton />
    </div>
  );
}
